/**
 * Часова шкала очікування області без React (design-system/stability.md, розд. 2): поріг 300 мс,
 * далі скелетон, з 3 с — «Довше, ніж зазвичай…», з 10 с — тайм-аут; скелетон, що вже з'явився,
 * тримається не менше 300 мс (baseline А4), інакше він блимає на відповіді за 320 мс.
 *
 * Окремо від хука `useAreaState`, щоб пороги перевіряти на `vi.useFakeTimers()`: хелпери рендера
 * з фейковими таймерами не працюють (test/render.tsx).
 */
import { PENDING_DELAY_MS, PENDING_SLOW_MS, PENDING_TIMEOUT_MS } from './Pending.tsx';

/** Мінімальний показ скелетона, що вже з'явився (baseline А4). */
export const AREA_MIN_SKELETON_MS = 300;

/**
 * - `idle` — область не чекає (дані є, запиту немає чи він вимкнений);
 * - `wait` — чекає, але менше за поріг: на екрані те, що було (або нічого на першому показі);
 * - `skeleton` / `slow` — скелетон області, `slow` — ще й «Довше, ніж зазвичай…»;
 * - `timeout` — відповіді немає 10 с: стан помилки зі «Спробувати ще раз».
 */
export type AreaPhase = 'idle' | 'wait' | 'skeleton' | 'slow' | 'timeout';

/** Таймери шкали: типово — глобальні; тест хука підставляє ручні. */
export interface AreaClock {
  now(): number;
  after(ms: number, run: () => void): () => void;
}

/** Поріг і вже відчекане до старту шкали (маршрут: роутер сам чекав `defaultPendingMs`). */
export interface AreaTimelineOptions {
  /** Мінімальний показ скелетона, що вже з'явився. */
  minSkeletonMs?: number;
  /** Поріг скелетона; типово 300 мс. Маршрут передає 0: поріг уже відчекав роутер. */
  thresholdMs?: number;
  /**
   * Скільки очікування вже минуло до першого `set(true)`: «довше» і тайм-аут рахуються від початку
   * очікування (переходу), а не від появи шкали. Діє лише на перше очікування, не на повтор.
   */
  waitedMs?: number;
}

export const realClock: AreaClock = {
  now: () => Date.now(),
  after: (ms, run) => {
    const timer = setTimeout(run, ms);
    return () => clearTimeout(timer);
  },
};

export class AreaTimeline {
  private current: AreaPhase = 'idle';
  /** Коли з'явився скелетон (`clock.now()`), `null` — скелетона немає. */
  private shownAt: number | null = null;
  /** Скелетон тримаємо до мінімального показу, хоча чекати вже нічого. */
  private holding = false;
  private cancels: Array<() => void> = [];
  private release: (() => void) | null = null;
  private readonly minSkeletonMs: number;
  private readonly thresholdMs: number;
  /** Відчекане до першого очікування; повтор і нове очікування після відповіді — з нуля. */
  private waited: number;

  constructor(
    private readonly onChange: () => void,
    private readonly clock: AreaClock = realClock,
    {
      minSkeletonMs = AREA_MIN_SKELETON_MS,
      thresholdMs = PENDING_DELAY_MS,
      waitedMs = 0,
    }: AreaTimelineOptions = {},
  ) {
    this.minSkeletonMs = minSkeletonMs;
    this.thresholdMs = thresholdMs;
    this.waited = waitedMs;
  }

  get phase(): AreaPhase {
    return this.current;
  }

  /** Скелетон на екрані, хоча очікування скінчилось: дані покаже наступна зміна. */
  get held(): boolean {
    return this.holding;
  }

  /**
   * Чи малювати скелетон зараз. `usable` — дані для показу вже є: тоді скелетон лишається, лише
   * поки не відбув мінімум показу (рендер іде раніше, ніж ефект повідомить шкалу про дані).
   */
  skeletonVisible(usable: boolean): boolean {
    if (this.holding) return true;
    if (this.current !== 'skeleton' && this.current !== 'slow') return false;
    if (!usable) return true;
    return this.shownAt !== null && this.clock.now() < this.shownAt + this.minSkeletonMs;
  }

  /** Область чекає (`true`) чи ні. Повторний виклик з тим самим значенням нічого не робить. */
  set(waiting: boolean): void {
    if (waiting) {
      if (this.current !== 'idle' && !this.holding) return;
      this.stopRelease();
      // Знову чекаємо під скелетоном, що ще тримається: він лишається, відлік — від початку.
      const resumed = this.holding;
      this.holding = false;
      this.current = resumed ? 'skeleton' : 'wait';
      // Поріг 0 (маршрут) — скелетон одразу: зміна фази без таймера теж перемальовує.
      if (this.schedule(resumed) || resumed) this.onChange();
      return;
    }
    if (this.current === 'idle' && !this.holding) return;
    this.stopTimers();
    const left = this.shownAt === null ? 0 : this.shownAt + this.minSkeletonMs - this.clock.now();
    if ((this.current === 'skeleton' || this.current === 'slow') && left > 0) {
      this.holding = true;
      this.release = this.clock.after(left, () => {
        this.release = null;
        this.finish();
      });
      return;
    }
    this.finish();
  }

  /** Почати відлік заново (повтор після тайм-ауту). */
  restart(): void {
    this.stopTimers();
    this.stopRelease();
    this.holding = false;
    this.shownAt = null;
    this.waited = 0;
    this.current = 'wait';
    this.schedule(false);
    this.onChange();
  }

  /** Зупинити таймери й повернутись у `idle`: наступний `set` почне відлік заново (StrictMode). */
  dispose(): void {
    this.stopTimers();
    this.stopRelease();
    this.holding = false;
    this.shownAt = null;
    this.current = 'idle';
  }

  /**
   * Таймери фаз від початку очікування (мінус уже відчекане). Фази, чий час уже настав, ставляться
   * одразу; повертає, чи фаза змінилась без таймера.
   */
  private schedule(skeletonShown: boolean): boolean {
    const to = (phase: AreaPhase) => () => {
      if (phase === 'skeleton') this.shownAt = this.clock.now();
      this.current = phase;
    };
    const steps: Array<[AreaPhase, number]> = [
      ...(skeletonShown ? [] : [['skeleton', this.thresholdMs] as [AreaPhase, number]]),
      ['slow', PENDING_SLOW_MS],
      ['timeout', PENDING_TIMEOUT_MS],
    ];
    let now = false;
    this.cancels = [];
    for (const [phase, at] of steps) {
      const left = at - this.waited;
      if (left > 0) {
        this.cancels.push(
          this.clock.after(left, () => {
            to(phase)();
            this.onChange();
          }),
        );
        continue;
      }
      to(phase)();
      now = true;
    }
    return now;
  }

  private finish() {
    const changed = this.current !== 'idle' && this.current !== 'wait';
    this.waited = 0;
    this.holding = false;
    this.shownAt = null;
    this.current = 'idle';
    // Із `wait` у `idle` на екрані нічого не міняється: перемальовує вже сама зміна даних.
    if (changed) this.onChange();
  }

  private stopTimers() {
    for (const cancel of this.cancels) cancel();
    this.cancels = [];
  }

  private stopRelease() {
    this.release?.();
    this.release = null;
  }
}
